import { Box, Button, CircularProgress, DialogActions, Typography, Alert } from '@mui/material'
import { FC } from 'react'
import { CustomModal } from '../../common/CustomModal'
import { AccountingPeriod, getMonthName } from './types'
import WarningIcon from '@mui/icons-material/Warning'

interface UncloseMonthModalProps {
  isOpen: boolean
  period: AccountingPeriod | null
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
}

export const UncloseMonthModal: FC<UncloseMonthModalProps> = ({
  isOpen,
  period,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  if (!period) return null

  return (
    <CustomModal title="Unclose Accounting Period" isOpen={isOpen} handleClose={onClose}>
      <Box sx={{ minWidth: 400 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Warning</Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to unclose{' '}
          <strong>
            {getMonthName(period.month)} {period.fiscalYear}
          </strong>
          .
        </Typography>

        {/* Special warning for December (year-end unclose) */}
        {period.month === 12 && (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Year-End Unclose Will Be Triggered
            </Typography>
            <Typography variant="body2" paragraph>
              Unclosing December will <strong>automatically unclose Year {period.fiscalYear}</strong>. The system will:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
              <Typography component="li" variant="body2">
                Remove the year-end close record (year will be OPEN)
              </Typography>
              <Typography component="li" variant="body2">
                Recalculate opening balances for {period.fiscalYear + 1}
              </Typography>
              <Typography component="li" variant="body2">
                Revert Retained Earnings (remove net income transfer)
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
              ⚠️ This operation will fail if any periods in {period.fiscalYear + 1} are closed.
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This action will:
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2">Remove the closed status from this period</Typography>
          </li>
          <li>
            <Typography variant="body2">Delete all account statements created during the close</Typography>
          </li>
          <li>
            <Typography variant="body2">Allow transactions to be posted for this period again</Typography>
          </li>
          {period.month === 12 && (
            <li>
              <Typography variant="body2" fontWeight="bold" color="error.main">
                Reopen Year {period.fiscalYear} for corrections
              </Typography>
            </li>
          )}
        </Box>

        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          <strong>Note:</strong> If you need to make adjustments to a closed year, consider using the "Closed-Year
          Journal Entry" feature instead of unclosing.
        </Typography>

        <DialogActions sx={{ padding: '20px 0 0', gap: 1 }}>
          <Button onClick={onClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>

          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={isLoading}
            endIcon={isLoading && <CircularProgress size={20} color="inherit" />}
          >
            {period.month === 12 ? 'Unclose December & Year' : 'Unclose Period'}
          </Button>
        </DialogActions>
      </Box>
    </CustomModal>
  )
}
